import { useEffect, useMemo, useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { useApp } from "../state/app";
import ReportSheet from "./ReportSheet";
import { fmtDT, fmtT, isoToSec, secToIso } from "../utils/period";

export interface FeedItem {
  kind: "trip" | "stop" | "start" | "dip" | "alarm" | "case";
  id: number | string; unit: number | null; ts: string; end_ts?: string | null;
  p_before?: number | null; f_min?: number | null; u_min?: number | null; u_max?: number | null; dur_s?: number | null; below_s?: number | null;
  details?: Record<string, unknown> | null;
  unit_label?: string; category?: string; type_key?: string; message?: string; plc_tag?: string | null; active?: boolean; acked?: boolean;
  key?: string; date?: string; time?: string;
}
interface CardOut {
  event: FeedItem;
  telemetry: { ts: string; rs: number | null; st: number | null; rt: number | null; p: number | null; q: number | null; f: number | null; ir: number | null; i_s: number | null; it: number | null }[];
  telemetry_unit: number | null;
  alarms: { id: number; ts: string; unit_label: string; category: string; message: string; active: boolean }[];
}

const n1 = (x: number | null | undefined, d = 1) => (x == null ? "—" : x.toFixed(d).replace(".", ","));
const n0 = (x: number | null | undefined) => (x == null ? "—" : String(Math.round(x)));

export function kindColor(k: string) {
  return k === "trip" ? "#ff453a" : k === "dip" ? "#ff9500" : k === "alarm" ? "#8593a0" : k === "case" ? "#bf5af2" : k === "start" ? "#34c759" : "var(--hint)";
}

/** One-line title + subtitle for a feed item (shared by the feed, Главная and the card). */
export function describe(it: FeedItem, t: (k: string, v?: Record<string, string | number>) => string): { title: string; sub: string } {
  const d = (it.details ?? {}) as Record<string, number | null | undefined>;
  switch (it.kind) {
    case "trip": return { title: t("ev.trip", { u: it.unit ?? "", kw: n0(it.p_before) }), sub: [it.f_min != null ? `f ${n1(it.f_min, 2)} Гц` : "", it.u_min != null ? `U ${n1(it.u_min)}–${n1(it.u_max)} кВ` : ""].filter(Boolean).join(" · ") };
    case "stop": return { title: t("ev.stop", { u: it.unit ?? "", kw: n0(it.p_before) }), sub: it.f_min != null ? `f ${n1(it.f_min, 2)} Гц` : "" };
    case "start": return { title: t("ev.start", { u: it.unit ?? "" }), sub: d.p_after_60s != null ? t("ev.start_sub", { kw: n0(d.p_after_60s as number) }) : "" };
    // ``thr`` — the dip norm the event was found with (step 5); older rows were found at 18 kV
    case "dip": return { title: t("ev.dip", { u: it.unit ?? "", kv: n1(it.u_min), s: it.dur_s ?? 0 }), sub: [t("ev.below", { s: it.below_s ?? 0, kv: String(d.thr ?? 18).replace(".", ",") }), d.q_max != null ? `Q ≤ ${n0(d.q_max as number)} квар` : "", d.i_max != null ? `I ≤ ${n0(d.i_max as number)} А` : ""].filter(Boolean).join(" · ") };
    case "alarm": {
      const cat = t("alarms.cat." + (it.category ?? "OTHER"));
      return { title: `${cat === "alarms.cat." + it.category ? it.category : cat}: ${it.message ?? ""}`, sub: [it.unit_label ?? "", it.active ? t("ev.active") : "", it.acked ? t("ev.acked") : ""].filter(Boolean).join(" · ") };
    }
    default: return { title: t("ev.case", { u: it.unit_label ?? "", d: it.date ?? "", h: it.time ?? "" }), sub: t("ev.case_sub") };
  }
}

function Tip({ active, payload }: { active?: boolean; payload?: { name?: string; value?: number; color?: string; payload?: { full?: string } }[] }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 8px", fontSize: 12 }}>
      <div style={{ fontWeight: 700 }}>{payload[0]?.payload?.full}</div>
      {payload.map(s => <div key={s.name} style={{ color: s.color }}>{s.name}: <b>{s.value == null ? "—" : s.value}</b></div>)}
    </div>
  );
}

// Bottom sheet for one event: numbers, ±60 s of telemetry (voltage phases +
// power), alarms around it, and the two actions — open in «Телеметрия», PDF.
export default function EventCard({ item, onClose, onOpenCase }: { item: FeedItem; onClose: () => void; onOpenCase?: (key: string) => void }) {
  const { t } = useT();
  const app = useApp();
  useTelegramBackButton(onClose, [onClose]);
  const [d, setD] = useState<CardOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pdf, setPdf] = useState(false);
  const isCase = item.kind === "case";
  useEffect(() => {
    if (isCase) return;
    const url = item.kind === "alarm" ? `/api/events/alarm/${item.id}` : `/api/events/${item.id}`;
    api.get<CardOut>(url).then(setD).catch((e: ApiError) => setErr(e.message));
  }, [item.id, item.kind, isCase]);

  const desc = describe(item, t);
  const unit = (d?.telemetry_unit ?? item.unit ?? 1) as number;
  // ±45 s: the whole event plus context, still a per-second PDF for dips up to 90 s
  const from = secToIso(isoToSec(item.ts) - 45), to = secToIso(isoToSec(item.end_ts ?? item.ts) + 45);
  const period = { kind: "custom" as const, from, to };
  const data = useMemo(() => (d?.telemetry ?? []).map(r => ({
    label: fmtT(r.ts), full: fmtDT(r.ts), RS: r.rs, ST: r.st, RT: r.rt, P: r.p, f: r.f,
  })), [d]);
  const mark = fmtT(item.ts);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "92vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
          <i style={{ width: 10, height: 10, borderRadius: "50%", background: kindColor(item.kind), flex: "none" }} />
          <h2 style={{ margin: 0, fontSize: 17, lineHeight: 1.25 }}>{desc.title}</h2>
        </div>
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 10 }}>{fmtDT(item.ts)}{item.end_ts ? ` → ${fmtT(item.end_ts)}` : ""}{desc.sub ? ` · ${desc.sub}` : ""}</div>
        {item.kind === "alarm" && item.plc_tag && <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 8 }}>{item.plc_tag}</div>}
        {err && <div className="alert error">{err}</div>}
        {!isCase && !d && !err && <div className="empty-state"><span className="spinner" /></div>}
        {d && data.length > 0 && (
          <>
            <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 2 }}>G{unit} · {t("ev.chart_u")}</div>
            <ResponsiveContainer width="100%" height={190}>
              <LineChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={30} />
                <YAxis tick={{ fontSize: 10 }} width={36} domain={["auto", "auto"]} />
                <Tooltip content={<Tip />} />
                <ReferenceLine x={mark} stroke="var(--hint)" strokeDasharray="2 4" />
                <Line type="monotone" dataKey="RS" stroke="#2481cc" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="ST" stroke="#ff9500" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="RT" stroke="#34c759" strokeWidth={1.5} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 12, color: "var(--hint)", margin: "6px 0 2px" }}>{t("ev.chart_p")}</div>
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} minTickGap={30} />
                <YAxis tick={{ fontSize: 10 }} width={36} domain={[0, "auto"]} />
                <Tooltip content={<Tip />} />
                <ReferenceLine x={mark} stroke="var(--hint)" strokeDasharray="2 4" />
                <Line type="monotone" dataKey="P" stroke="#2481cc" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
        {d && data.length === 0 && !isCase && <div style={{ fontSize: 13, color: "var(--hint)", margin: "6px 0" }}>{t("ev.no_telemetry")}</div>}
        {d && d.alarms.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 4 }}>{t("ev.alarms_around")}</div>
            {d.alarms.slice(0, 12).map(a => (
              <div key={a.id} style={{ display: "flex", gap: 8, fontSize: 12, padding: "4px 0", borderTop: "1px solid var(--border)" }}>
                <span style={{ color: "var(--hint)", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmtT(a.ts)}</span>
                <span style={{ minWidth: 0 }}>{a.unit_label} · {a.message}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {isCase ? (
            <button className="btn" onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); onOpenCase?.(String(item.key)); onClose(); }}>{t("ev.open_case")}</button>
          ) : (
            <>
              <button className="btn" onClick={() => { tg()?.HapticFeedback?.impactOccurred("light"); app.openTelemetry(unit === 2 ? "g2" : "g1", period); onClose(); }}>{t("ev.open_telemetry")}</button>
              {(d?.telemetry.length ?? 0) > 0 && <button className="btn pdf" style={{ maxWidth: 110 }} onClick={() => setPdf(true)}>📄 PDF</button>}
            </>
          )}
        </div>
        <button className="btn secondary" style={{ marginTop: 8 }} onClick={onClose}>{t("common.close")}</button>
        {pdf && <ReportSheet unit={unit} sel={period} catalog={null} onClose={() => setPdf(false)} />}
      </div>
    </div>
  );
}
